import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Linkedin, Send, Building2, MapPin, ExternalLink,
  Target, Flame, ThermometerSun, ThermometerSnowflake, Mail, CheckCircle2, Clock,
  ShieldCheck, History, Wallet, Users, Image as ImageIcon, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import ApolloSearchPanel from "./ApolloSearchPanel";
import ContactBuilderPanel from "./ContactBuilderPanel";
import PageVisitorsPanel from "./PageVisitorsPanel";

const TIER_STYLE = {
  high:   { label: "Hot", bg: "bg-emerald-500/10 text-emerald-500 border-emerald-500/40", icon: Flame },
  medium: { label: "Warm", bg: "bg-amber-accent/10 text-amber-accent border-amber-accent/40", icon: ThermometerSun },
  low:    { label: "Cold", bg: "bg-muted/30 text-muted-foreground border-border", icon: ThermometerSnowflake },
};

export default function LeadDiscoveryPage() {
  const [tab, setTab] = useState("apollo");
  const [url, setUrl] = useState("");
  const [enriching, setEnriching] = useState(false);
  const [current, setCurrent] = useState(null);  // {prospect_id, summary, drafts}
  const [noteOverride, setNoteOverride] = useState("");
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/leads/recent?limit=15");
      setRecent(data.prospects || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadRecent(); }, []);

  const enrich = async () => {
    if (!url.trim()) {
      toast.error("Paste a LinkedIn URL");
      return;
    }
    setEnriching(true);
    setCurrent(null);
    try {
      const { data } = await api.post("/leads/enrich", { url: url.trim() });
      setCurrent(data);
      setNoteOverride(data?.drafts?.invite_note || "");
      toast.success(`✓ Enriched: ${data.summary.full_name} — ${data.drafts.fit_tier} fit (${data.drafts.fit_score})`);
      loadRecent();
    } catch (e) {
      toast.error(`Enrich failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setEnriching(false);
  };

  const sendInvite = async () => {
    if (!current?.prospect_id) return;
    setSending(true);
    try {
      await api.post(`/leads/${current.prospect_id}/send-invite`, {
        note: noteOverride.trim() || null,
      });
      toast.success("LinkedIn invitation sent via Unipile ✓");
      setCurrent((c) => c && ({ ...c, _sent: true }));
      loadRecent();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSending(false);
  };

  const noteLen = noteOverride.length;

  return (
    <DashboardLayout
      eyebrow="Lead Generation"
      title="AI Lead Discovery"
    >
      <div className="inline-flex items-center gap-1 p-1 bg-muted/30 border border-border rounded-sm mb-6" data-testid="ld-tabs">
        <button
          type="button"
          data-testid="ld-tab-apollo"
          onClick={() => setTab("apollo")}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
            tab === "apollo" ? "bg-signal-500 text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Users className="w-3.5 h-3.5" /> Bulk Apollo Search
        </button>
        <button
          type="button"
          data-testid="ld-tab-builder"
          onClick={() => setTab("builder")}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
            tab === "builder" ? "bg-signal-500 text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <ImageIcon className="w-3.5 h-3.5" /> Build from image / list
        </button>
        <button
          type="button"
          data-testid="ld-tab-paste"
          onClick={() => setTab("paste")}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
            tab === "paste" ? "bg-signal-500 text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Linkedin className="w-3.5 h-3.5" /> Paste LinkedIn URL
        </button>
        <button
          type="button"
          data-testid="ld-tab-visitors"
          onClick={() => setTab("visitors")}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
            tab === "visitors" ? "bg-signal-500 text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Eye className="w-3.5 h-3.5" /> Page Visitors
        </button>
      </div>

      {tab === "apollo" && <ApolloSearchPanel />}

      {tab === "builder" && <ContactBuilderPanel />}

      {tab === "visitors" && <PageVisitorsPanel />}

      {tab === "paste" && (
      <>
      <div className="bg-signal-500/5 border border-signal-500/20 rounded-sm p-3 mb-6 flex gap-3" data-testid="ld-callout">
        <Sparkles className="w-4 h-4 text-signal-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/80 leading-relaxed">
          <strong className="text-signal-500">Paste a LinkedIn profile URL</strong> from any decision-maker at your strategic accounts (Logwin, Kerry, Noatum, YCH) or any CEE manufacturer.
          AI scores them against your Europacific ICP and drafts a personalised invite — ready to send via Unipile in one click. No bulk scraping, no Sales Navigator required.
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 space-y-4">
          <div className="data-card">
            <div className="label-eyebrow inline-flex items-center gap-2 mb-3">
              <Linkedin className="w-3.5 h-3.5 text-signal-500" /> Step 1 — Paste profile URL
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="ld-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/john-doe-12345/"
                className="font-mono text-xs"
                onKeyDown={(e) => { if (e.key === "Enter") enrich(); }}
              />
              <Button onClick={enrich} disabled={enriching} data-testid="ld-enrich-btn" className="rounded-sm whitespace-nowrap">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {enriching ? "Enriching…" : "Enrich"}
              </Button>
            </div>
          </div>

          {current && (
            <>
              <ProfileCard summary={current.summary} drafts={current.drafts} erpRelationship={current.erp_relationship} outreachBlocked={current.outreach_blocked} blockReason={current.outreach_block_reason} />

              {current.outreach_blocked ? (
                <div className="data-card border-amber-accent/40" data-testid="ld-blocked-state">
                  <div className="label-eyebrow text-amber-accent inline-flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Outreach blocked — ERP handles this customer
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {current.erp_relationship?.bookings_count} prior booking{current.erp_relationship?.bookings_count === 1 ? "" : "s"} found in the ERP.
                    The ERP&apos;s reactivation flow will engage them — LogiGrowth stays out of the way to avoid spam.
                  </p>
                </div>
              ) : (
                <div className="data-card space-y-3" data-testid="ld-invite-composer">
                  <div className="flex items-center justify-between">
                    <div className="label-eyebrow inline-flex items-center gap-2">
                      <Send className="w-3.5 h-3.5" /> Step 2 — Review & send invite
                    </div>
                    <span className={`text-[0.6rem] font-mono ${noteLen > 280 ? "text-destructive" : "text-muted-foreground"}`}>
                      {noteLen} / 280
                    </span>
                  </div>
                  <textarea
                    data-testid="ld-note-input"
                    value={noteOverride}
                    onChange={(e) => setNoteOverride(e.target.value.slice(0, 300))}
                    rows={4}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
                  />
                  <div className="flex gap-2 items-center">
                    <Button
                      onClick={sendInvite}
                      disabled={sending || current._sent || noteLen > 280}
                      data-testid="ld-send-invite-btn"
                      className="rounded-sm"
                    >
                      {current._sent ? (
                        <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Invite sent</>
                      ) : (
                        <><Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send LinkedIn invite"}</>
                      )}
                    </Button>
                    <p className="text-[0.65rem] text-muted-foreground">
                      Daily cap: 20 invites · we&apos;ll auto-throttle.
                    </p>
                  </div>
                </div>
              )}

              {(current.drafts?.dm || current.drafts?.email_body) && (
                <div className="data-card space-y-3" data-testid="ld-followup-previews">
                  <div className="label-eyebrow">Step 3 — Drafted follow-ups (use after they accept)</div>
                  {current.drafts.dm && (
                    <div>
                      <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                        <Linkedin className="w-3 h-3" /> Day +3 LinkedIn DM
                      </div>
                      <pre className="whitespace-pre-wrap text-xs bg-muted/30 border border-border rounded-sm p-3 font-sans">{current.drafts.dm}</pre>
                    </div>
                  )}
                  {current.drafts.email_subject && (
                    <div>
                      <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> Day +10 cold email
                      </div>
                      <div className="text-xs bg-muted/30 border border-border rounded-sm p-3 space-y-2">
                        <div><span className="font-semibold">Subject:</span> {current.drafts.email_subject}</div>
                        <pre className="whitespace-pre-wrap font-sans">{current.drafts.email_body}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Recent enrichments */}
        <div className="space-y-2">
          <div className="label-eyebrow inline-flex items-center gap-2 mb-1">
            <Target className="w-3.5 h-3.5" /> Recent leads
          </div>
          {recent.length === 0 ? (
            <p className="text-xs text-muted-foreground italic px-1">No leads yet — paste a URL to start.</p>
          ) : (
            recent.map((r) => {
              const Tier = TIER_STYLE[r.fit_tier] || TIER_STYLE.medium;
              const Icon = Tier.icon;
              return (
                <div key={r.id} className="data-card !p-3" data-testid={`ld-recent-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{r.full_name}</div>
                      <div className="text-[0.65rem] text-muted-foreground truncate">{r.title}</div>
                      <div className="text-[0.65rem] text-signal-500 truncate inline-flex items-center gap-1">
                        <Building2 className="w-2.5 h-2.5" /> {r.company || "—"}
                      </div>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded-sm text-[0.55rem] tracking-widest uppercase font-mono border inline-flex items-center gap-1 ${Tier.bg}`}>
                      <Icon className="w-2.5 h-2.5" /> {Tier.label} {r.score}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[0.6rem] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> {relativeTime(r.updated_at || r.created_at)}
                    </span>
                    {r.status === "invited" && (
                      <span className="text-emerald-500 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Invited
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      </>
      )}
    </DashboardLayout>
  );
}

function ProfileCard({ summary, drafts, erpRelationship, outreachBlocked, blockReason }) {
  const Tier = TIER_STYLE[drafts.fit_tier] || TIER_STYLE.medium;
  const Icon = Tier.icon;
  const hasErp = erpRelationship && (erpRelationship.in_contacts || erpRelationship.offers_count > 0 || erpRelationship.bookings_count > 0);
  return (
    <div className="data-card" data-testid="ld-profile-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-base">{summary.full_name}</div>
          <div className="text-xs text-muted-foreground">{summary.title}</div>
          <div className="text-xs text-signal-500 inline-flex items-center gap-1.5 mt-1">
            <Building2 className="w-3 h-3" /> {summary.company || "—"}
            {summary.location && (
              <>
                <span className="text-muted-foreground mx-1">·</span>
                <MapPin className="w-3 h-3" /> {summary.location}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-1 rounded-sm text-[0.65rem] tracking-widest uppercase font-mono border inline-flex items-center gap-1.5 ${Tier.bg}`}>
            <Icon className="w-3 h-3" /> {Tier.label} fit · {drafts.fit_score}/100
          </span>
          {summary.linkedin_url && (
            <a
              href={summary.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[0.6rem] text-muted-foreground hover:text-signal-500 inline-flex items-center gap-1"
              data-testid="ld-profile-link"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Open on LinkedIn
            </a>
          )}
        </div>
      </div>

      {hasErp && (
        <div className={`mb-3 rounded-sm border p-2.5 text-xs ${outreachBlocked ? "bg-amber-accent/5 border-amber-accent/40" : "bg-signal-500/5 border-signal-500/40"}`} data-testid="ld-erp-relationship">
          <div className="flex items-center gap-2 font-semibold text-foreground mb-1.5">
            <History className="w-3.5 h-3.5" /> Prior ERP relationship found
          </div>
          <div className="grid grid-cols-2 gap-2 text-[0.7rem] text-foreground/80">
            {erpRelationship.in_contacts && (
              <div>✓ In ERP contacts</div>
            )}
            {erpRelationship.offers_count > 0 && (
              <div>📨 {erpRelationship.offers_count} prior offer{erpRelationship.offers_count === 1 ? "" : "s"}</div>
            )}
            {erpRelationship.bookings_count > 0 && (
              <div>📦 {erpRelationship.bookings_count} booking{erpRelationship.bookings_count === 1 ? "" : "s"}</div>
            )}
            {erpRelationship.total_revenue_eur > 0 && (
              <div className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> €{Math.round(erpRelationship.total_revenue_eur).toLocaleString("en-US")} lifetime</div>
            )}
          </div>
          {outreachBlocked && (
            <div className="mt-2 text-[0.65rem] text-amber-accent">Outreach blocked: {blockReason}</div>
          )}
        </div>
      )}

      <div className="text-xs text-foreground/80 italic mb-3 border-l-2 border-signal-500/40 pl-3">
        {drafts.reasoning}
      </div>

      {drafts.signals?.length > 0 && (
        <div className="flex gap-1 flex-wrap mb-3" data-testid="ld-signals">
          {drafts.signals.map((s) => (
            <span key={s} className="px-1.5 py-0.5 text-[0.6rem] font-mono uppercase tracking-widest bg-muted/30 border border-border rounded-sm">
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
